import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import PageBanner from '../../components/ui/PageBanner';
import SectionHeading from '../../components/ui/SectionHeading';
import Button from '../../components/ui/Button';
import contactezNous from '../../assets/images/ContactezNous.jpg';
import { 
  PhoneIcon, 
  EnvelopeIcon, 
  MapPinIcon,
  BuildingOfficeIcon,
  AcademicCapIcon,
  UserGroupIcon,
  BookOpenIcon
} from '@heroicons/react/24/outline';

const locations = [
  {
    id: 'maternelle',
    title: 'Maternelle',
    icon: AcademicCapIcon,
    address: 'N°55, Rue 27, Oulfa, Casablanca',
    phone: '05 22 89 52 24',
    email: 'maternelle@gssanaa.com',
    color: 'bg-pink-100'
  },
  {
    id: 'primaire',
    title: 'Primaire',
    icon: BookOpenIcon,
    address: '362 Bd Ouad Oum Rabii, Oulfa',
    phone: '05 22 90 82 38',
    email: 'primaire@gssanaa.com',
    color: 'bg-blue-100'
  },
  {
    id: 'college',
    title: 'Collège',
    icon: BuildingOfficeIcon,
    address: 'N°31, Rue 27, Oulfa, Casablanca',
    phone: '05 22 89 52 40',
    email: 'college@gssanaa.com',
    color: 'bg-green-100'
  },
  {
    id: 'lycee',
    title: 'Lycée',
    icon: UserGroupIcon,
    address: 'N°5 et 7, Angle Rue 49 et 50 Groupe "U", Oulfa, Casablanca',
    phone: '05 22 93 04 08',
    email: 'lycee@gssanaa.com',
    color: 'bg-purple-100'
  },
];

const ContactPage = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // In a real implementation, this would send the form data to a server
    console.log('Form data submitted:', formData);
    setFormSubmitted(true);

    // Reset form after submission
    setTimeout(() => {
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
      });
      setFormSubmitted(false);
    }, 5000);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <PageBanner
        title="Contactez-nous"
        subtitle="Nous sommes à votre écoute"
        imageSrc={contactezNous}
      />

      {/* Contact Information Cards */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <SectionHeading
            title="Nos coordonnées"
            subtitle="Trouvez-nous facilement"
            centered={true}
          />
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mt-12">
            {locations.map((location, index) => (
              <motion.div
                key={location.id}
                className={`rounded-lg shadow-elegant p-6 ${location.color}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 rounded-full bg-white flex items-center justify-center">
                    <location.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="ml-3 text-lg font-semibold">{location.title}</h3>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-start">
                    <MapPinIcon className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">{location.address}</span>
                  </div>
                  <div className="flex items-start">
                    <PhoneIcon className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                    <span className="text-gray-700">{location.phone}</span>
                  </div>
                  <div className="flex items-start">
                    <EnvelopeIcon className="h-5 w-5 text-primary mt-0.5 mr-2 flex-shrink-0" />
                    <a href={`mailto:${location.email}`} className="text-gray-700 hover:text-primary">
                      {location.email}
                    </a>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-start">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <SectionHeading
                title="Envoyez-nous un message"
                subtitle="Comment pouvons-nous vous aider ?"
              />
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Vous avez des questions sur nos programmes, nos frais de scolarité ou notre processus d'admission ? N'hésitez pas à nous contacter en remplissant le formulaire ci-dessous. Notre équipe vous répondra dans les plus brefs délais.
              </p>
              
              {formSubmitted ? (
                <motion.div
                  className="bg-green-50 border-l-4 border-green-500 p-4 mb-8"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  <p className="text-green-700">
                    Votre message a été envoyé avec succès. Nous vous répondrons dès que possible.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1">
                        Prénom *
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1">
                        Nom *
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                        Téléphone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                      Sujet *
                    </label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={6}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                    ></textarea>
                  </div>
                  
                  <div>
                    <Button type="submit" variant="primary" size="large">
                      Envoyer le message
                    </Button>
                  </div>
                </form>
              )}
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
            >
              <div className="sticky top-24">
                <div className="bg-white p-6 rounded-lg shadow-elegant mb-8">
                  <h3 className="text-xl font-semibold mb-4 border-b border-gray-200 pb-2">Heures d'ouverture</h3>
                  <ul className="space-y-3">
                    <li className="flex justify-between">
                      <span className="text-gray-700">Lundi - Vendredi:</span>
                      <span className="font-medium">8h00 - 18h00</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-gray-700">Samedi:</span>
                      <span className="font-medium">8h00 - 13h00</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-gray-700">Dimanche:</span>
                      <span className="font-medium">Fermé</span>
                    </li>
                  </ul>
                </div>
                
                <div className="h-80 bg-gray-200 rounded-lg overflow-hidden">
                   <iframe 
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d831.2082597095172!2d-7.683347011334203!3d33.55771403247783!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xda62cb35a9423f9%3A0x6d9a6ffc6d2b8ce0!2sGroupe%20Scolaire%20Sanaa!5e0!3m2!1sfr!2sma!4v1747925850595!5m2!1sfr!2sma"
                      width="100%" 
                      height="100%" 
                      style={{ border: 0 }} 
                      allowFullScreen="" 
                      loading="lazy" 
                      referrerPolicy="no-referrer-when-downgrade"
                      title="Groupe Scolaire Sanaa Location"
                    ></iframe>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <SectionHeading
            title="Questions fréquentes"
            subtitle="Besoin d'aide ?"
            centered={true}
          />
          
          <div className="max-w-3xl mx-auto mt-12">
            <div className="space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
              >
                <h3 className="text-xl font-semibold mb-2">Comment puis-je inscrire mon enfant à votre école ?</h3>
                <p className="text-gray-700">
                  Pour inscrire votre enfant, veuillez contacter notre service administratif ou remplir le formulaire de contact sur cette page. Nous vous fournirons toutes les informations nécessaires concernant les documents requis et la procédure à suivre.
                </p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 }}
              >
                <h3 className="text-xl font-semibold mb-2">Quels sont les frais de scolarité ?</h3>
                <p className="text-gray-700">
                  Les frais de scolarité varient selon le niveau d'enseignement. Pour obtenir des informations détaillées, veuillez contacter notre service administratif qui vous fournira un barème complet des frais pour l'année scolaire en cours.
                </p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <h3 className="text-xl font-semibold mb-2">Proposez-vous un service de transport scolaire ?</h3>
                <p className="text-gray-700">
                  Oui, nous offrons un service de transport scolaire pour les élèves dans plusieurs quartiers de Casablanca. Pour connaître les itinéraires desservis et les tarifs, veuillez contacter notre administration.
                </p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <h3 className="text-xl font-semibold mb-2">Quels sont les horaires de l'école ?</h3>
                <p className="text-gray-700">
                  Les horaires varient selon le niveau d'enseignement. En général, les cours commencent à 8h00 et se terminent entre 16h00 et 18h00, selon la classe et le jour de la semaine. Un emploi du temps détaillé est fourni au début de l'année scolaire.
                </p>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.4 }}
              >
                <h3 className="text-xl font-semibold mb-2">Comment puis-je prendre rendez-vous pour visiter l'école ?</h3>
                <p className="text-gray-700">
                  Pour organiser une visite, veuillez contacter notre administration ou remplir le formulaire de contact sur cette page en précisant que vous souhaitez visiter notre établissement. Nous vous proposerons des dates disponibles pour vous accueillir.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default ContactPage;